/**
 * 스크립트 테스트 하니스 — 결정 #30(테스트 환경) / #4. 트리거 스크립트(JS·Lua)를
 * **실제 대화 없이 결정적으로** 검증하기 위한 모킹 capability API와 어서션 평가기다.
 *
 * 핵심 설계: 새 실행 경로를 만들지 않는다. 이 모킹 API({@link MockCapabilityApi})를
 * 실제 ScriptHost(QuickJS/Lua 샌드박스)에 그대로 주입하면, 스크립트가 프로덕션과
 * 동일하게 실행되되 변수·메시지·난수·시계가 모두 통제·기록된다. 따라서 입력→출력,
 * 변수 변화, 로그를 결정적으로 어서션할 수 있다.
 *
 * 플랫폼 중립(결정 #10): wasmoon/QuickJS 의존 없음. 순수 자료구조라 테스트로 사양을
 * 고정한다. 실제 실행(invoke)은 web 러너가 담당한다.
 */
import type { ScriptCapabilityApi } from "../ports/script-host.js";

export type VarScope = "chat" | "character" | "global";

/** 모킹 채팅 메시지(getChatMessages가 돌려줄 값). */
export interface MockChatMessage {
  role: string;
  content: string;
  name?: string;
}

export interface MockApiOptions {
  /** 시작 변수 상태(스코프별). 스크립트가 getVar로 읽고 setVar로 바꾼다. */
  vars?: Partial<Record<VarScope, Record<string, unknown>>>;
  /** getChatMessages가 돌려줄 모킹 대화(최신순 가정). */
  chatMessages?: MockChatMessage[];
  /** 결정적 RNG 시드(기본 1). 같은 시드는 항상 같은 난수열을 준다. */
  seed?: number;
  /** now()가 돌려줄 고정 시각(ms, 기본 0). */
  now?: number;
  /** 시작 스토리지 상태(storageGet/Set, 권한 있을 때만 노출). */
  storage?: Record<string, unknown>;
}

/** 실행 후 검사할 수 있는 모킹 API 스냅샷. */
export interface MockApiSnapshot {
  vars: Record<VarScope, Record<string, unknown>>;
  logs: unknown[][];
  storage: Record<string, unknown>;
  /** 스크립트가 setResult를 호출했는지(JS 경로). Lua는 호스트 훅을 쓰므로 무관. */
  resultViaApi?: string;
}

/** mulberry32 — 작고 결정적인 PRNG(시드 재현 가능, 결정 #4). */
function makePrng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function emptyVars(): Record<VarScope, Record<string, unknown>> {
  return { chat: {}, character: {}, global: {} };
}

/**
 * ScriptCapabilityApi의 결정적 모킹 구현(결정 #30). 실제 샌드박스에 주입해 스크립트를
 * 돌리면, 모든 부수효과(변수 쓰기·로그)가 여기에 기록되어 어서션 대상이 된다.
 *
 * - safe 등급(getVar/setVar/getChatMessages/setResult/log/random/now)을 완전 구현.
 * - elevated 등급: storageGet/Set는 인메모리로 구현(테스트 편의). fetch/generateImage/
 *   sendPrompt는 기본 미노출(권한 모델과 일치) — 필요하면 옵션으로 주입한다.
 */
export class MockCapabilityApi implements ScriptCapabilityApi {
  private readonly vars: Record<VarScope, Record<string, unknown>>;
  private readonly messages: MockChatMessage[];
  private readonly rng: () => number;
  private readonly fixedNow: number;
  private readonly store: Record<string, unknown>;
  private readonly logs: unknown[][] = [];
  private resultViaApi: string | undefined;

  constructor(opts: MockApiOptions = {}) {
    const base = emptyVars();
    this.vars = {
      chat: { ...base.chat, ...(opts.vars?.chat ?? {}) },
      character: { ...base.character, ...(opts.vars?.character ?? {}) },
      global: { ...base.global, ...(opts.vars?.global ?? {}) },
    };
    this.messages = opts.chatMessages ? [...opts.chatMessages] : [];
    this.rng = makePrng(opts.seed ?? 1);
    this.fixedNow = opts.now ?? 0;
    this.store = { ...(opts.storage ?? {}) };
  }

  // --- safe tier ---
  getVar(scope: VarScope, key: string): unknown {
    return this.vars[scope]?.[key];
  }
  setVar(scope: VarScope, key: string, value: unknown): void {
    if (!this.vars[scope]) return;
    this.vars[scope][key] = value;
  }
  getChatMessages(limit?: number): MockChatMessage[] {
    const all = this.messages;
    if (typeof limit === "number" && limit >= 0 && limit < all.length) {
      return all.slice(all.length - limit);
    }
    return [...all];
  }
  setResult(text: string): void {
    this.resultViaApi = text;
  }
  log(...args: unknown[]): void {
    this.logs.push(args);
  }
  random(): number {
    return this.rng();
  }
  now(): number {
    return this.fixedNow;
  }

  // --- elevated tier (인메모리 모킹) ---
  async storageGet(key: string): Promise<unknown> {
    return this.store[key];
  }
  async storageSet(key: string, value: unknown): Promise<void> {
    this.store[key] = value;
  }

  /** 실행 후 상태 스냅샷(어서션·UI 표시용). */
  snapshot(): MockApiSnapshot {
    return {
      vars: {
        chat: { ...this.vars.chat },
        character: { ...this.vars.character },
        global: { ...this.vars.global },
      },
      logs: this.logs.map((l) => [...l]),
      storage: { ...this.store },
      ...(this.resultViaApi !== undefined ? { resultViaApi: this.resultViaApi } : {}),
    };
  }
}

// --- 어서션 ---

/** 테스트 케이스의 기대값(선언적). 지정한 항목만 검사한다. */
export interface ScriptAssertion {
  /** 출력 텍스트가 정확히 일치해야 함. */
  expectText?: string;
  /** 출력 텍스트에 포함돼야 함. */
  expectTextContains?: string;
  /** 출력 텍스트가 포함하면 안 됨. */
  expectTextNotContains?: string;
  /** 특정 변수의 최종값(JSON 동등 비교). */
  expectVars?: { scope: VarScope; key: string; value: unknown }[];
}

export interface AssertionResult {
  pass: boolean;
  /** 실패한 항목 설명(빈 배열 = 통과). */
  failures: string[];
}

/** JSON 동등 비교(순서 무관 깊은 비교의 단순 버전). */
function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * 출력 텍스트 + 모킹 API 스냅샷을 기대값과 대조한다(결정 #30). 지정된 항목만 검사하며,
 * 하나라도 어긋나면 pass=false + 사람이 읽는 실패 사유를 돌려준다.
 */
export function evaluateAssertions(
  outputText: string,
  snapshot: MockApiSnapshot,
  a: ScriptAssertion,
): AssertionResult {
  const failures: string[] = [];

  if (a.expectText !== undefined && outputText !== a.expectText) {
    failures.push(`출력 텍스트가 기대와 다릅니다.\n  기대: ${JSON.stringify(a.expectText)}\n  실제: ${JSON.stringify(outputText)}`);
  }
  if (a.expectTextContains !== undefined && !outputText.includes(a.expectTextContains)) {
    failures.push(`출력에 "${a.expectTextContains}"가 포함돼야 합니다.`);
  }
  if (a.expectTextNotContains !== undefined && outputText.includes(a.expectTextNotContains)) {
    failures.push(`출력에 "${a.expectTextNotContains}"가 포함되면 안 됩니다.`);
  }
  for (const ev of a.expectVars ?? []) {
    const actual = snapshot.vars[ev.scope]?.[ev.key];
    if (!jsonEqual(actual, ev.value)) {
      failures.push(
        `변수 ${ev.scope}.${ev.key}가 기대와 다릅니다.\n  기대: ${JSON.stringify(ev.value)}\n  실제: ${JSON.stringify(actual)}`,
      );
    }
  }

  return { pass: failures.length === 0, failures };
}
